// Emergency billing — per EMERGENCY_BILLING_APIS.md.
//
// ER is billed like IPD but keyed on the emergency visit rather than an
// admission: a bed-less EMERGENCY billing encounter and account are opened
// against the visit, and charges are posted into the ordinary billing pipeline
// from there. Emergency beds are not special-cased on the billing side — a bed
// charge is a normal service-catalogue row.
//
// Shares BASE / authHeaders / unwrap with emergencyApi.js rather than repeating
// the tenant-header and envelope convention; kept in its own file because
// billing is a separate concern from the clinical record and emergencyApi.js is
// already long.

import axios from "axios";

import { BASE, authHeaders, unwrap } from "./emergencyApi";
import { buildLedger, normalizeCharge, normalizeService } from "../utils/charges";

const json = { "Content-Type": "application/json" };

/** Every emergency bed charge carries this service_type. */
export const EMERGENCY_BED_SERVICE_TYPE = "EMERGENCY_BED";

/**
 * Search the service catalogue for something to charge.
 *
 * Bed charges are selected by `service_type`. The `classification_name` filter
 * the billing doc suggests now works too and returns the identical four rows —
 * either is correct, and this one is already what the dialog passes. Neither is
 * a numeric id: `service_classification.id` differs per tenant schema, so a
 * hardcoded id charges the wrong thing on the next hospital. `service_type` also
 * answers with no search term, which is what lets the dialog show the four bed
 * charges the moment it opens.
 *
 * Not `searchMedicalServices()` from emergencyApi.js, which hits the same
 * endpoint for the investigation-order picker: that one pins episode_type=OPD,
 * which excludes the ER bed rows outright (they are episode_type=ER), and
 * returns neither `service_code` nor `fee`, both of which charging needs.
 * Different questions of the same catalogue, so they stay separate rather than
 * growing flags.
 */
export async function searchChargeableServices({ name, serviceType } = {}) {
  const params = {};
  if (name) params.name = name;
  if (serviceType) params.service_type = serviceType;

  const res = await axios.get(`${BASE}search_medical_services/`, {
    headers: authHeaders(),
    params,
  });
  const body = unwrap(res);
  const rows = body.data || body.results || [];
  return rows.map(normalizeService).filter((s) => s.code || s.id);
}

/**
 * Open the visit's billing account, or return the one it already has.
 *
 * Idempotent by design on the server, which is what lets the Charges tab call
 * it on the way to the first charge instead of making the clerk press a button
 * whose purpose only a billing person would understand.
 */
export async function openEmergencyBilling(visitId, payload = {}) {
  const res = await axios.post(
    `${BASE}emergency/visits/${visitId}/billing-setup/`,
    payload,
    { headers: authHeaders(json) }
  );
  const body = unwrap(res);
  return {
    billingAccountId: body.billing_account_id || null,
    billingEncounterId: body.billing_encounter_id || null,
    accountNumber: body.data?.billing_account_number || null,
    encounterNumber: body.data?.encounter_number || null,
    alreadyLinked: Boolean(body.data?.already_linked),
  };
}

/**
 * The visit's charge ledger — the raw event stream, CHARGE and CANCELLATION
 * rows together, ordered and interpreted by `buildLedger`.
 *
 * NOT in EMERGENCY_BILLING_APIS.md, which ships only the three POSTs. Added on
 * our side because capture returns a charge_event id and reversal consumes one:
 * without a read-back those ids are lost on reload and a mistaken charge could
 * never be corrected.
 *
 * A visit nobody has charged has no billing account, and the server says so with
 * `billing_open: false` and an empty list rather than an error — so an untouched
 * visit reads as empty rather than as broken.
 */
export async function listEmergencyCharges(visitId) {
  const res = await axios.get(
    `${BASE}emergency/visits/${visitId}/billing/charges/`,
    { headers: authHeaders() }
  );
  const body = unwrap(res);
  return {
    charges: buildLedger((body.data || []).map(normalizeCharge)),
    billingOpen: body.billing_open !== false,
    billingAccountId: body.billing_account_id || null,
    // May this user add or reverse charges (emergency_charge_adjust or billing access).
    canAdjust: body.can_adjust === true,
  };
}

/**
 * Post charges onto the visit.
 *
 * Always the bulk form, even for one line: each line then succeeds or fails on
 * its own and the caller can say which one to fix, where a single capture that
 * fails halfway through a set leaves no such record.
 *
 * @param {Array<{code?, id?, quantity?, amount?, remarks?}>} lines
 */
export async function captureEmergencyCharges(visitId, lines) {
  const charges = lines.map((line) => ({
    ...(line.code ? { service_code: line.code } : { medical_service_id: line.id }),
    quantity: Number(line.quantity) || 1,
    // Only when the clerk typed one. Sending the catalogue fee back as an
    // override would freeze today's price into a charge that should track it.
    ...(line.amount === "" || line.amount === null || line.amount === undefined
      ? {}
      : { amount: Number(line.amount) }),
    ...(line.remarks ? { remarks: line.remarks } : {}),
  }));

  const res = await axios.post(
    `${BASE}emergency/visits/${visitId}/billing/charge-capture/`,
    { charges },
    { headers: authHeaders(json) }
  );
  return unwrap(res);
}

/**
 * Post the visit's open charges to billing. Idempotent; cheap when nothing is
 * open. Resolves with what happened, for screens that show it.
 */
export async function postPendingCharges(visitId) {
  const res = await axios.post(
    `${BASE}emergency/visits/${visitId}/billing/post-pending/`,
    {},
    { headers: authHeaders(json) }
  );
  const body = unwrap(res);
  return { rows: body.data || [], summary: body.summary || {} };
}

/**
 * Fire-and-forget post of the visit's open charges, after any clinical action.
 * Never throws and never delays the clinical flow: whatever does not post
 * stays in the visit's Charge check.
 */
export function firePendingCharges(visitId) {
  if (!visitId) return;
  postPendingCharges(visitId).catch(() => {});
}

/**
 * Move an Admitted visit's ED charges onto its inpatient bill. Idempotent: with
 * no admission yet, or nothing left to move, it changes nothing. Resolves with
 * {status, admission_id, moved, failed, errors}.
 */
export async function transferToAdmission(visitId) {
  const res = await axios.post(
    `${BASE}emergency/visits/${visitId}/billing/transfer-to-admission/`,
    {},
    { headers: authHeaders(json) }
  );
  return unwrap(res).data || {};
}

/**
 * Fire-and-forget move of an Admitted visit's charges onto the inpatient bill.
 * The admission usually does not exist yet at close; the billing screens try
 * again when they open it.
 */
export function fireTransferToAdmission(visitId) {
  if (!visitId) return;
  transferToAdmission(visitId).catch(() => {});
}

/** The visit's Procedures & equipment entries, each with its charge status. */
export async function listVisitServices(visitId) {
  const res = await axios.get(`${BASE}emergency/visits/${visitId}/services/`, { headers: authHeaders() });
  return unwrap(res).data || [];
}

/**
 * Record a procedure, equipment or specialist on the visit; its charge is
 * recorded with it. Payload: {kind, medical_service_id | doctor_id,
 * performed_by_id?, notes?}.
 */
export async function recordVisitService(visitId, payload) {
  const res = await axios.post(`${BASE}emergency/visits/${visitId}/services/`, payload, {
    headers: authHeaders(json),
  });
  const body = unwrap(res);
  return { record: body.data, message: body.message };
}

/** Remove an entry (soft); its charge is cancelled, or reversed if it already posted. */
export async function removeVisitService(visitId, recordId, reason) {
  const res = await axios.delete(`${BASE}emergency/visits/${visitId}/services/${recordId}/`, {
    headers: authHeaders(json),
    data: { reason },
  });
  const body = unwrap(res);
  return { message: body.message, warning: body.warning || "" };
}

/**
 * Choose the stocked item for a dose given with none; the server prices it
 * from the pharmacy catalogue and records its charge. Post pending afterwards.
 */
export async function priceDose(visitId, { administrationId, itemId, itemName, batchNumber, quantity }) {
  const res = await axios.post(
    `${BASE}emergency/visits/${visitId}/billing/dose-price/`,
    {
      administration_id: administrationId,
      item_id: itemId,
      item_name: itemName,
      batch_number: batchNumber,
      quantity,
    },
    { headers: authHeaders(json) }
  );
  return unwrap(res);
}

/** What has not reached billing: open charges, and doses with no item chosen. */
export async function getChargeCheck(visitId) {
  const res = await axios.get(`${BASE}emergency/visits/${visitId}/billing/charge-check/`, {
    headers: authHeaders(),
  });
  const data = unwrap(res).data || {};
  return {
    open: data.open || [],
    dosesToPrice: data.doses_to_price || [],
    canAdjust: data.can_adjust === true,
  };
}

/** Mark a dose given with no stocked item as not charged, with the reason why. */
export async function waiveDose(visitId, { administrationId, reason }) {
  const res = await axios.post(
    `${BASE}emergency/visits/${visitId}/billing/dose-waive/`,
    { administration_id: administrationId, reason },
    { headers: authHeaders(json) }
  );
  return unwrap(res);
}

/**
 * Reverse charges. Audit-safe on the server: nothing is deleted — a pending
 * charge is ignored, a processed one gets a linked cancellation that backs the
 * account totals out. The reason is stored on the cancellation, so it is
 * required rather than optional.
 */
export async function reverseEmergencyCharges(visitId, chargeEventIds, reason) {
  const res = await axios.post(
    `${BASE}emergency/visits/${visitId}/billing/charge-reverse/`,
    { charge_event_ids: chargeEventIds, reason },
    { headers: authHeaders(json) }
  );
  return unwrap(res);
}

/**
 * Pharmacy-catalogue items for a dose, each batch priced per issue unit by the
 * server. For choosing only — the server prices the batch again when it records
 * the charge.
 */
export async function searchMedicinePrices(visitId, query) {
  const q = (query || "").trim();
  if (q.length < 2) return [];
  const res = await axios.get(`${BASE}emergency/visits/${visitId}/billing/medicine-prices/`, {
    headers: authHeaders(),
    params: { q },
  });
  return unwrap(res).data || [];
}
